import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import { useSelectedCompany } from '../../hooks/index';
import { StakeholderOwnershipSummary } from '../../types/stakeholder.types';
import { formatDisplayValue } from '../../utils/format-display-value.util';

type SummaryItemProps = {
  className?: string;
  title: ReactNode | string;
  value: string;
};

const SummaryItem: FC<SummaryItemProps> = ({ title, value, className }) => (
  <div
    className={twMerge(
      'flex h-20 w-full flex-col justify-center gap-2 rounded-lg bg-gray-50 px-2 py-3 lg:flex-grow lg:basis-[40%]',
      className,
    )}
  >
    <span className="text-nowrap text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-500">
      {title}
    </span>
    <span className="text-base font-semibold leading-8 text-gray-600">{value}</span>
  </div>
);

export type OwnershipSummaryProps = Partial<StakeholderOwnershipSummary>;

export const OwnershipSummary: FC<OwnershipSummaryProps> = ({
  companyOwned,
  investment,
  sharePrice,
  numberOfShares,
  roi,
  valueOfShares,
  votingPercentage,
}) => {
  const { selectedCompany } = useSelectedCompany();
  const currency = selectedCompany?.currency;

  const currencySymbol = currency?.symbol || currency?.iso3?.toUpperCase() || '';

  return (
    <div className="flex w-full flex-grow flex-col gap-3 rounded-lg bg-gray-50 p-1 pt-3 lg:w-[30%] lg:min-w-[350px]">
      <span className="px-4 text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
        <AppFormattedMessage id="OWNERSHIP_SUMMARY" />
      </span>
      <div className="flex w-full flex-col gap-3 rounded-md bg-white p-3 shadow-sm lg:flex-row lg:flex-wrap">
        <SummaryItem
          title={<AppFormattedMessage id="NUMBER_OF_SHARES" />}
          value={formatDisplayValue(numberOfShares)}
        />
        <SummaryItem
          title={<AppFormattedMessage id="COMPANY_OWNED" />}
          value={formatDisplayValue(companyOwned, { multiply: 100, suffix: '%' })}
        />
        <SummaryItem
          title={<AppFormattedMessage id="VOTING" />}
          value={formatDisplayValue(votingPercentage, { multiply: 100, suffix: '%' })}
        />
        <SummaryItem
          title={<AppFormattedMessage id="SHARES_PRICE" />}
          value={formatDisplayValue(sharePrice, { prefix: currencySymbol })}
        />
        <SummaryItem
          title={<AppFormattedMessage id="VALUE_OF_SHARES" />}
          value={formatDisplayValue(valueOfShares, { prefix: currencySymbol })}
        />
        <SummaryItem
          title={
            <>
              <AppFormattedMessage id="INVESTMENT" />
              {` (${currencySymbol})`}
            </>
          }
          value={formatDisplayValue(investment, { prefix: currencySymbol })}
        />
        <SummaryItem
          className="lg:basis-[50%]"
          title={<AppFormattedMessage id="ROI" />}
          value={formatDisplayValue(roi, { suffix: 'x' })}
        />
      </div>
    </div>
  );
};
